import React from 'react';
import type { PreviewData } from './leadsTypes';

type LeadsPreviewModalProps = {
  previewData: PreviewData;
  filterRunId: string;
  runName: string | undefined;
  uploading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const LeadsPreviewModal: React.FC<LeadsPreviewModalProps> = ({
  previewData,
  filterRunId,
  runName,
  uploading,
  onCancel,
  onConfirm,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      <h2 className="text-xl font-bold text-white mb-4">
        Preview Leads Upload (
        {previewData.file.name.endsWith('.xlsx') || previewData.file.name.endsWith('.xls')
          ? 'Excel'
          : 'CSV'}
        )
      </h2>

      {previewData.errors.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-400 text-sm font-semibold mb-2">Warnings:</p>
          <ul className="text-yellow-300 text-xs space-y-1">
            {previewData.errors.map((error, idx) => (
              <li key={idx}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4">
        <p className="text-gray-300 text-sm mb-2">
          <span className="font-semibold">{previewData.leads.length}</span> leads will be uploaded
          {filterRunId ? ` to campaign: ${runName || 'Selected Campaign'}` : ' (Leads Upload)'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar mb-4">
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-cyan-400 uppercase">Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-cyan-400 uppercase">Phone</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-cyan-400 uppercase">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {previewData.leads.slice(0, 50).map((lead, idx) => (
                <tr key={idx} className="hover:bg-white/5">
                  <td className="px-3 py-2 text-gray-300">{lead.name}</td>
                  <td className="px-3 py-2 text-gray-300">{lead.phone}</td>
                  <td className="px-3 py-2 text-gray-300">{lead.email || '-'}</td>
                </tr>
              ))}
              {previewData.leads.length > 50 && (
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-center text-gray-400 text-xs">
                    ... and {previewData.leads.length - 50} more leads
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-white/10">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploading}
          className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={uploading || previewData.leads.length === 0}
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : `Upload ${previewData.leads.length} Leads`}
        </button>
      </div>
    </div>
  </div>
);

export default LeadsPreviewModal;
